import { isEqual, mergeWith, uniq } from 'lodash';
import BaseService from '@core/common/BaseService';
import { umsClient } from '@core/clients/ums/UMSClient';
import conversationsHistoryService from '@core/services/conversations-history/ConversationsHistoryService';
import {
  getParticipants,
  getLastContentEventNotification,
  transformParticipantsByIdRole,
  INCATranscriptTransformer,
  UMSTranscriptTransformer,
} from './transcript-transformer';

export const EVENTS = {
  ON_MESSAGE_RECEIVED: 'onMessageReceived',
  ON_PARTICIPANTS_READY: 'onParticipantsReady',
  ON_CONVERSATION_SUBSCRIBE_ERROR: 'onConversationSubscribeError',
};

const SETTINGS = {
  CONVERSATION_HISTORY_DEFAULTS: {
    offset: 0,
    limit: 100,
  },
  UMS_DEFAULTS: {
    maxQuantity: 500,
    newerThan: 0,
    numCallbacks: 2,
    timeout: 30000,
    from: 0,
  },
};

class TranscriptService extends BaseService {
  constructor() {
    super({ name: 'TranscriptService' });
    this.umsClient = umsClient;
    this.subscriptionId = null;
    this.hoursSettings = null;
    this.participantsMap = {
      current: null,
      all: null,
    };
    this.participants = null;
  }

  /**
   * Initialise UMS connection
   */
  init() {
    if (
      !this.umsClient.isConnected
    ) {
      try {
        this.umsClient.connect()
          .catch(error => this.logError(error));
      } catch (error) {
        this.throwError({ message: error.message });
      }
    }
  }

  /**
   * Subscribe to conversation on UMS
   * @param params {Object}
   * @param params.brandId {String} - brandName (id)
   * @param params.conversationId {String}
   * @param params.consumerId {String}
   */
  async subscribeToConversation(params) {
    const { brandId, conversationId, consumerId } = params;
    if (this.subscriptionId) {
      await this.unsubscribeFromConversation();
    }
    try {
      // here we get conversation's participants before we subscribe to conversation
      // this is done to render all the participants names
      // since UMS returns only active participants and does not reflect transfers/takeovers
      this.participantsMap.all = await conversationsHistoryService.getConversationParticipants({
        brandId,
        conversationId,
        consumerId,
      });
    } catch (error) {
      this.throwError({ message: error.message });
    }
    this.umsClient.subscribeConversations({ brandId, conversationId })
      .then(({ subscriptionId }) => {
        this.subscriptionId = subscriptionId;
      })
      .catch((error) => {
        this.emit(EVENTS.ON_CONVERSATION_SUBSCRIBE_ERROR, error);
        this.throwError({ message: 'unable to subscribe to conversation' });
      });
    this.umsClient.on(
      this.umsClient.umsConnection.EVENTS.onExConversationNotification,
      data => this.handleExConversation(data),
      'TranscriptServiceExConversationNotification',
    );
  }

  /**
   * Unsubscribe from conversation on UMS
   */
  async unsubscribeFromConversation() {
    if (this.subscriptionId) {
      try {
        await this.umsClient.unsubscribeConversations({ subscriptionId: this.subscriptionId });
        this.resetTranscript();
        this.umsClient.clearAllListeners(
          this.umsClient.umsConnection.EVENTS.onExConversationNotification,
        );
        this.clearAllListeners(EVENTS.ON_PARTICIPANTS_READY);
        this.clearAllListeners(EVENTS.ON_MESSAGE_RECEIVED);
        this.clearAllListeners(EVENTS.ON_CONVERSATION_SUBSCRIBE_ERROR);
      } catch (error) {
        this.throwError({ message: error.message });
      }
    }
  }

  /**
   * Handles exConversationNotification event of UMS
   * @param eventData - UMS response object
   */
  async handleExConversation(eventData) {
    await this.setParticipants(eventData);
    this.handleIncomingMessage(eventData);
  }

  /**
   * Sets participants of the conversation
   * @param data - eventData response object from UMS
   */
  setParticipants(data) {
    const participants = getParticipants(data);
    if (!isEqual(this.participantsMap.current, participants)) {
      this.participantsMap.current = participants;
      this.participantsMap.all = mergeWith(
        this.participantsMap.all,
        participants,
        (objValue = [], srcValue) => uniq(objValue.concat(srcValue)));
      this.getParticipantsData()
        .then((participantsMap) => { this.participants = participantsMap; })
        .then(() => this.emit(EVENTS.ON_PARTICIPANTS_READY))
        .catch(error => this.throwError({ message: error.message }));
    }
  }

  /**
   * Fetches participants details from UMS by using this.participantsMap
   * @return {Promise<unknown>}
   */
  getParticipantsData() {
    const participants = transformParticipantsByIdRole(this.participantsMap.all);
    return new Promise((resolve) => {
      // make an array of requests to UMS.getUserProfile
      // run the requests in parallel with Promise.all
      const requests = Object
        .keys(participants)
        .map(participantId => this.umsClient.getUserProfile({ userId: participantId }));
      Promise.all(requests)
        .then((profiles) => {
          const participantsMapped = profiles.reduce((acc, profile) => {
            acc[profile.userId] = {
              ...acc[profile.userId],
              firstName: profile.firstName,
              lastName: profile.lastName,
              nickName: profile.nickName,
            };
            return acc;
          }, participants);
          resolve(participantsMapped);
        });
    });
  }

  /**
   * Gets messages from UMS records and transforms them into Transcript
   * @param conversationId
   * @param dialogId
   * @param from - message sequence to start from
   * @return {Promise<LeTranscriptModel>}
   */
  async getConversationTranscript({
    conversationId,
    dialogId,
    from = SETTINGS.UMS_DEFAULTS.from,
  }) {
    const messages = await this.umsClient.newQueryMessages({
      conversationId,
      dialogId,
      maxQuantity: SETTINGS.UMS_DEFAULTS.maxQuantity,
      newerThan: from,
      numCallbacks: SETTINGS.UMS_DEFAULTS.numCallbacks,
      timeout: SETTINGS.UMS_DEFAULTS.timeout,
    });
    return UMSTranscriptTransformer.transform(
      messages,
      this.participants,
      { hoursSettings: this.hoursSettings },
    );
  }

  /**
   * Get conversation history and transforms it into Transcript
   * @param brandName
   * @param conversationId
   * @param consumerId
   * @param offset
   * @param limit
   * @return {Promise<LeTranscriptModel>}
   */
  async getHistoryTranscript({
    brandName,
    conversationId,
    consumerId,
    offset,
    limit,
  }) {
    const historyRecords = await conversationsHistoryService.getConversationsHistory({
      brandId: brandName,
      conversationId,
      consumerId,
      offset,
      limit, // limit does not work -> api returns all the messages
    });
    return INCATranscriptTransformer.transform(
      historyRecords,
      { hoursSettings: this.hoursSettings },
    );
  }

  /**
   * Handles incoming messages
   * Extract lastContentEventNotification object from eventData
   * @param data - eventData response object from UMS
   */
  handleIncomingMessage(data) {
    const message = getLastContentEventNotification(data);
    const { messages } = UMSTranscriptTransformer.transform(
      message,
      this.participants,
      { hoursSettings: this.hoursSettings },
    );
    this.emit(EVENTS.ON_MESSAGE_RECEIVED, messages[0]);
  }

  /**
   * Sets hours setting
   * @param settings
   */
  setHoursSettings(settings) {
    if (!this.hoursSettings && settings !== this.hoursSettings) {
      this.hoursSettings = settings;
    }
  }

  resetTranscript() {
    this.subscriptionId = null;
    this.participants = null;
    this.participantsMap = { current: null, all: null };
  }
}

const transcriptService = new TranscriptService();

export default transcriptService;
